import React, { useEffect, useState } from "react";
import { useAtom } from "jotai/index";
import {
  isSettingOpenAtom,
  standbyModeAtom,
  volumeAtom,
  isBubbleVisibleAtom,
  isConversationInterruptedAtom,
} from "~/Atom";
import { PlaySound } from "~/SoundUtils";
import { conversationEmitter } from "~/components/ControlButton";
import HamburgerMenu from "~/components/settings/HamburgerMenu";
import Modal from "~/components/modal/Modal";
import GoogleMap from "~/components/modal/GoogleMapModal";
import FloorMap from "~/components/modal/FloorMap";
import QRCode from "~/components/modal/QRCode";
import ProblemOccurs from "~/components/modal/ProblemOccurs";
import Explanation from "~/components/modal/Explanation";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import ExpandPanel from "~/components/ExpandPanel";
import MenuToggleButton from "~/components/settings/HamburgerMenu/MenuToggleButton";
import "~/css/Modal.css";
import "~/css/SideBar.css";
import { useTranslation } from "react-i18next";

/**
 * スタンバイモードの状態を表す定数
 */
const standbyMode = {
  off: 0,
  voiceRecognition: 2,
} as const;

const SideBar: React.FC = () => {
  const [volume] = useAtom(volumeAtom);
  const [isGoogleMapModalOpen, setIsGoogleMapModalOpen] = useState(false);
  const [isFloorModalOpen, setIsFloorModalOpen] = useState(false);
  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [isProblemOccursModalOpen, setIsProblemOccursModalOpen] = useState(false);
  const [, setStandbyMode] = useAtom(standbyModeAtom);
  const [isSettingOpen, setIsSettingOpen] = useAtom(isSettingOpenAtom);
  const [isReloadModalOpen, setIsReloadModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [, setIsBubbleVisible] = useAtom(isBubbleVisibleAtom);
  const [isConversationInterruptEnabled] = useAtom(isConversationInterruptedAtom);
  const { t } = useTranslation();

  /**
   * モーダル操作の共通処理 - オープン
   * サウンドを再生してモーダルを開く
   */
  const handleModalOpen = (setModalState: React.Dispatch<React.SetStateAction<boolean>>) => {
    PlaySound("push", volume);
    setModalState(true);
  };

  /**
   * モーダル操作の共通処理 - クローズ
   * サウンドを再生してモーダルを閉じ、音声認識を再起動する
   */
  const handleModalClose = (setModalState: React.Dispatch<React.SetStateAction<boolean>>) => {
    PlaySound("push", volume);
    setModalState(false);
    setStandbyMode(isSettingOpen ? standbyMode.off : standbyMode.voiceRecognition);
  };

  /**
   * 拡張サイドメニュー トグル
   */
  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  /**
   * 拡張メニュー経由で各モーダルを展開
   */
  const openViaPanel = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setIsExpanded(false);
    handleModalOpen(setter);
  };

  /**
   * 拡張パネルから設定を展開
   */
  const openSettingsViaPanel = () => {
    setIsExpanded(false);
    PlaySound("push", volume);
    setIsSettingOpen(true);
    setStandbyMode(standbyMode.off);
  };

  // 設定画面が開いたら拡張パネルを閉じる
  useEffect(() => {
    if (isSettingOpen && isExpanded) {
      setIsExpanded(false);
    }
  }, [isSettingOpen, isExpanded]);

  /**
   * ページをリロードする
   */
  const reload = () => {
    window.location.reload();
  };

  /**
   * 設定トグル
   */
  const handleSettingsToggle = () => {
    const newOpen = !isSettingOpen;
    setStandbyMode(newOpen ? standbyMode.off : standbyMode.voiceRecognition);
    setIsSettingOpen(newOpen);
    setIsBubbleVisible(false);
    PlaySound("push", volume);
  };

  /**
   * 拡張サイドメニューアイコンクリック
   * 設定が開いている場合は設定を閉じてから拡張を開く
   */
  const handleExpandIconClick = () => {
    if (isSettingOpen) {
      setIsSettingOpen(false);
      setStandbyMode(standbyMode.voiceRecognition);
      setIsExpanded(true);
      PlaySound("push", volume);
    } else {
      toggleExpanded();
      PlaySound("push", volume);
    }
  };

  /**
   * 拡張パネルを閉じる
   */
  const handleExpandClose = () => {
    PlaySound("cancel2", volume);
    setIsExpanded(false);
  };

  /**
   * 会話中断ボタンのハンドラ
   * 実処理はControlButton内で実装
   */
  const handleConversationInterrupt = () => {
    PlaySound("cancel2", volume);
    // 会話中断イベントを発火
    conversationEmitter.emit("interrupt");
  };

  return (
    <>
      <Modal
        title={t("areaMap")}
        isOpen={isGoogleMapModalOpen}
        onClose={() => handleModalClose(setIsGoogleMapModalOpen)}
      >
        <GoogleMap />
      </Modal>
      <Modal
        title={t("floorMap")}
        isOpen={isFloorModalOpen}
        onClose={() => handleModalClose(setIsFloorModalOpen)}
      >
        <FloorMap />
      </Modal>
      <Modal
        title={t("explanation")}
        isOpen={isExplanationModalOpen}
        onClose={() => handleModalClose(setIsExplanationModalOpen)}
      >
        <Explanation />
      </Modal>
      <ConfirmationModal
        show={isReloadModalOpen}
        handleClose={() => handleModalClose(setIsReloadModalOpen)}
        handleConfirm={reload}
        title={t("reloadTitle")}
        body={t("reloadBody")}
        cancelText={t("reloadCancelText")}
        confirmText={t("reloadConfirmText")}
      />
      <Modal
        title={t("qrCode")}
        isOpen={isQRCodeModalOpen}
        onClose={() => handleModalClose(setIsQRCodeModalOpen)}
      >
        <QRCode />
      </Modal>
      <Modal
        title={t("problemOccurs")}
        isOpen={isProblemOccursModalOpen}
        onClose={() => handleModalClose(setIsProblemOccursModalOpen)}
      >
        <ProblemOccurs />
      </Modal>
      {/* 拡張サイドパネル */}
      {!isSettingOpen && (
        <ExpandPanel
          isOpen={isExpanded}
          onClose={handleExpandClose}
          items={[
            {
              key: "map",
              icon: "distance",
              label: t("areaMap"),
              onClick: () => openViaPanel(setIsGoogleMapModalOpen),
            },
            {
              key: "floor",
              icon: "map",
              label: t("floorMap"),
              onClick: () => openViaPanel(setIsFloorModalOpen),
            },
            {
              key: "help",
              icon: "help",
              label: t("explanation"),
              onClick: () => openViaPanel(setIsExplanationModalOpen),
            },
            {
              key: "reload",
              icon: "cached",
              label: t("reloadTitle"),
              onClick: () => openViaPanel(setIsReloadModalOpen),
            },
            {
              key: "qr",
              icon: "qr_code",
              label: t("qrCode"),
              onClick: () => openViaPanel(setIsQRCodeModalOpen),
            },
            {
              key: "problem",
              icon: "construction",
              label: t("problemOccurs"),
              onClick: () => openViaPanel(setIsProblemOccursModalOpen),
            },
            {
              key: "settings",
              icon: "settings",
              label: t("settings"),
              onClick: openSettingsViaPanel,
            },
          ]}
        />
      )}

      {/* サイドバー（設定画面表示中も表示） */}
      <div className="side-bar">
        <div className="side-bar-button">
          {/* サイドメニュー拡張アイコン */}
          <button
            className="btn btn-circle expand-btn horizontal-only"
            onClick={handleExpandIconClick}
            aria-expanded={isExpanded}
            aria-label="toggle expand menu"
          >
            <span className="material-symbols-rounded side-bar-expand-icons">
              {isExpanded ? "right_panel_close" : "left_panel_close"}
            </span>
          </button>

          {/* Googleマップアイコン */}
          <button
            className="btn btn-circle"
            onClick={() => handleModalOpen(setIsGoogleMapModalOpen)}
          >
            <span className="material-symbols-rounded side-bar-icons">distance</span>
          </button>

          {/* フロアマップアイコン */}
          <button className="btn btn-circle" onClick={() => handleModalOpen(setIsFloorModalOpen)}>
            <span className="material-symbols-rounded side-bar-icons">map</span>
          </button>

          {/* はてなアイコン */}
          <button
            className="btn btn-circle"
            onClick={() => handleModalOpen(setIsExplanationModalOpen)}
          >
            <span className="material-symbols-rounded side-bar-icons">help</span>
          </button>

          {/* 更新ボタン */}
          <button className="btn btn-circle" onClick={() => handleModalOpen(setIsReloadModalOpen)}>
            <span className="material-symbols-rounded side-bar-icons">cached</span>
          </button>

          {/* QRボタン */}
          <button className="btn btn-circle" onClick={() => handleModalOpen(setIsQRCodeModalOpen)}>
            <span className="material-symbols-rounded side-bar-icons">qr_code</span>
          </button>

          {/* 不具合発生修復手順ボタン */}
          <button
            className="btn btn-circle"
            onClick={() => handleModalOpen(setIsProblemOccursModalOpen)}
          >
            <span className="material-symbols-rounded side-bar-icons">construction</span>
          </button>

          {/* 会話中断ボタン */}
          <button
            className={`btn btn-circle ${isConversationInterruptEnabled ? "interrupt-btn-enabled" : "interrupt-btn-disabled"}`}
            onClick={() => handleConversationInterrupt()}
            disabled={!isConversationInterruptEnabled}
          >
            <span className="material-symbols-rounded side-bar-icons">play_disabled</span>
          </button>
        </div>
        {/* 設定トグルボタン */}
        <div className="side-bar-bottom">
          <div className="side-bar-button">
            <MenuToggleButton onClick={handleSettingsToggle} disabled={false} />
          </div>
        </div>
      </div>

      {/* 設定 */}
      <HamburgerMenu />
    </>
  );
};

export default SideBar;
